/**
 * UranIA PA — Pannello allegati skill (admin).
 *
 * Lista + upload + delete dei file allegati a una skill. Quando la skill
 * viene attivata via ``skill_hint`` nel Gateway, gli allegati vengono
 * iniettati come metadata in ``reasoning.rules`` (vedi
 * ``ai_gateway/routes/invocations.py::_fetch_skill_attachments``).
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';

import {
  deleteSkillAttachment,
  listSkillAttachments,
  SkillsApiError,
  uploadSkillAttachment,
} from '../../lib/admin/skills-api.ts';
import type {
  SkillAttachmentDto,
  SkillAttachmentListDto,
} from '../../lib/admin/skills-api.ts';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/json',
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function mimeShort(mime: string): string {
  const map: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'text/plain': 'TXT',
    'text/csv': 'CSV',
    'text/markdown': 'MD',
    'application/json': 'JSON',
  };
  return map[mime] ?? mime;
}

export interface SkillAttachmentsPanelProps {
  readonly skillId: string;
}

export function SkillAttachmentsPanel(props: SkillAttachmentsPanelProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileInputId = useId();
  const descrId = useId();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [descrizione, setDescrizione] = useState('');

  const queryKey = ['admin', 'skill-attachments', props.skillId];
  const query = useQuery<SkillAttachmentListDto, SkillsApiError>({
    queryKey,
    queryFn: () => listSkillAttachments(props.skillId),
    staleTime: 15_000,
  });

  const uploadMut = useMutation<SkillAttachmentDto, SkillsApiError, { file: File; descrizione?: string }>({
    mutationFn: async ({ file, descrizione: d }) =>
      uploadSkillAttachment(props.skillId, d === undefined ? { file } : { file, descrizione: d }),
    onSuccess: () => {
      setSelectedFile(null);
      setDescrizione('');
      void qc.invalidateQueries({ queryKey });
    },
  });

  const deleteMut = useMutation<void, SkillsApiError, string>({
    mutationFn: async (filename) => deleteSkillAttachment(props.skillId, filename),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (selectedFile === null) return;
    const descrTrimmed = descrizione.trim();
    uploadMut.mutate(
      descrTrimmed.length > 0
        ? { file: selectedFile, descrizione: descrTrimmed }
        : { file: selectedFile },
    );
  }

  function handleDelete(filename: string): void {
    if (window.confirm(t('admin.skills.attachments.confirm_delete', { filename }))) {
      deleteMut.mutate(filename);
    }
  }

  const items = query.data?.items ?? [];

  return (
    <div className="card shadow-sm border-0" data-testid="admin-skill-attachments">
      <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('admin.skills.attachments.header')}
        </span>
        <span
          className="badge"
          style={{
            backgroundColor: '#e7f1ff',
            color: '#084298',
            border: '1px solid #b6d4fe',
            fontSize: '0.7rem',
          }}
        >
          {items.length}
        </span>
      </div>
      <div className="card-body">
        <p className="small text-muted mb-3">
          {t('admin.skills.attachments.subtitle')}
        </p>

        {/* Form upload */}
        <form onSubmit={handleSubmit} className="mb-3" data-testid="admin-skill-attachments-form">
          <div className="row g-2 align-items-end">
            <div className="col-md-7">
              <label htmlFor={fileInputId} className="form-label small fw-semibold">
                {t('admin.skills.attachments.file_label')}
              </label>
              <input
                id={fileInputId}
                type="file"
                className="form-control form-control-sm"
                accept={ACCEPTED_TYPES.join(',')}
                onChange={(e): void => {
                  const f = e.target.files?.[0] ?? null;
                  setSelectedFile(f);
                }}
                disabled={uploadMut.isPending}
                data-testid="admin-skill-attachments-file"
              />
            </div>
            <div className="col-md-5">
              <label htmlFor={descrId} className="form-label small fw-semibold">
                {t('admin.skills.attachments.descr_label')}
              </label>
              <input
                id={descrId}
                type="text"
                className="form-control form-control-sm"
                value={descrizione}
                onChange={(e): void => setDescrizione(e.target.value)}
                placeholder={t('admin.skills.attachments.descr_placeholder')}
                maxLength={300}
                disabled={uploadMut.isPending}
              />
            </div>
            <div className="col-12 d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-sm btn-primary"
                disabled={selectedFile === null || uploadMut.isPending}
                data-testid="admin-skill-attachments-submit"
              >
                {uploadMut.isPending
                  ? t('admin.skills.attachments.uploading')
                  : t('admin.skills.attachments.upload')}
              </button>
            </div>
          </div>
          {uploadMut.isError && (
            <div className="alert alert-danger mt-2 mb-0 small" role="alert">
              <strong>{uploadMut.error.message}</strong>
              {uploadMut.error.detail !== null && (
                <p className="mb-0">{uploadMut.error.detail}</p>
              )}
            </div>
          )}
        </form>

        {/* Lista allegati */}
        {query.isLoading && (
          <p className="text-muted small" role="status">
            {t('admin.skills.attachments.loading')}
          </p>
        )}
        {query.isSuccess && items.length === 0 && (
          <p className="text-muted small fst-italic mb-0">
            {t('admin.skills.attachments.empty')}
          </p>
        )}
        {items.length > 0 && (
          <div className="list-group list-group-flush">
            {items.map((it) => (
              <div
                key={it.filename}
                className="list-group-item px-0 py-2"
                data-testid={`admin-skill-attachment-${it.filename}`}
              >
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <code className="small fw-semibold">{it.filename}</code>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: '#e9ecef',
                          color: '#495057',
                          fontSize: '0.62rem',
                        }}
                      >
                        {mimeShort(it.mime_type)}
                      </span>
                      <span className="small text-muted">{formatBytes(it.size_bytes)}</span>
                    </div>
                    {it.descrizione.length > 0 && (
                      <p className="small text-muted mb-1">{it.descrizione}</p>
                    )}
                    <p className="small text-muted mb-0" style={{ fontSize: '0.72rem' }}>
                      {t('admin.skills.attachments.uploaded_at')} {formatDate(it.uploaded_at)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={(): void => handleDelete(it.filename)}
                    disabled={deleteMut.isPending}
                    data-testid={`admin-skill-attachment-delete-${it.filename}`}
                  >
                    {t('admin.skills.attachments.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
